import { useEffect, useState, useCallback } from 'react'

export interface MeasuredElement {
  /** Callback ref to attach to the element being measured. */
  ref: (node: HTMLElement | null) => void
  /** The currently attached element, or null before it mounts. */
  element: HTMLElement | null
  /** Rendered width in px. 0 means "not measured yet", not "zero wide". */
  width: number
}

/**
 * Tracks the rendered width of an element in px.
 *
 * Deliberately a **callback ref**, not a `RefObject`. A `useRef` object is
 * identity-stable, so an effect keyed on it runs exactly once — and these
 * containers sit behind loading early-returns, so on a cold load the element
 * does not exist at first commit. The effect would early-return on a null
 * `ref.current` and never run again, leaving the width pinned at 0 forever
 * (and, after a remount, an observer still attached to a detached node).
 * Keying on the element itself means mount, unmount and swap all re-run it.
 *
 * `width` is 0 until the first measurement; callers must read that as
 * "unknown" and no-op rather than treating it as a real width — see
 * `resolvePaneWidths`.
 */
export function useElementWidth(): MeasuredElement {
  const [element, setElement] = useState<HTMLElement | null>(null)
  const [width, setWidth] = useState(0)

  const ref = useCallback((node: HTMLElement | null) => {
    setElement(node)
  }, [])

  useEffect(() => {
    if (!element) {
      setWidth(0)
      return
    }

    const measure = () => setWidth(element.getBoundingClientRect().width)
    measure()

    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', measure)
      return () => window.removeEventListener('resize', measure)
    }

    const observer = new ResizeObserver(measure)
    observer.observe(element)
    return () => observer.disconnect()
  }, [element])

  return { ref, element, width }
}
